import React, { useState, useRef } from 'react';
import { useForceLightTheme } from '../contexts/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Check, Eye, EyeOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  useForceLightTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=code, 3=new password
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const codeRefs = useRef([]);

  const handleFindAccount = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setUsername(response.data.username);
      setStep(2);
      toast.success('Reset code sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No account found with this email');
    } finally { setLoading(false); }
  };

  const handleCodeInput = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      codeRefs.current[5]?.focus();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) { toast.error('Enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-reset-code`, { email: email.trim(), code: fullCode });
      setResetToken(res.data.reset_token);
      setStep(3);
      toast.success('Code verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: email.trim(), new_password: newPassword, reset_token: resetToken });
      toast.success('Password reset successfully!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  const inputCls = "h-12 rounded-lg border bg-white/80 focus:bg-white transition-colors";
  const inputStyle = { borderColor: 'var(--fairway-sand)', color: '#000', WebkitTextFillColor: '#000' };

  const titles = {
    1: { heading: 'Forgot Password?', sub: 'Enter your email to get a reset code' },
    2: { heading: 'Enter Code', sub: `We sent a 6-digit code to ${email}` },
    3: { heading: 'New Password', sub: `Welcome back, ${username}!` },
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" style={{ background: 'var(--club-cream)' }} data-testid="forgot-password-page">
      <div className="w-full max-w-sm animate-fade-in">
        <button
          onClick={() => step === 1 ? navigate('/login') : setStep(step - 1)}
          className="flex items-center gap-1.5 text-sm mb-8 hover:opacity-70 transition-opacity"
          style={{ color: 'var(--warm-gray)' }}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-col items-center mb-10">
          <img src="/logo-the-club.png" alt="The Club" className="h-20 w-auto mb-6" data-testid="forgot-logo" />
          <h1 className="font-serif text-3xl font-semibold tracking-tight" style={{ color: 'var(--deep-forest)' }}>
            {titles[step].heading}
          </h1>
          <p className="text-sm mt-1 text-center" style={{ color: 'var(--warm-gray)' }}>
            {titles[step].sub}
          </p>
        </div>

        <div className="rounded-xl p-6 border" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleFindAccount} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                  <Input type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required className={`${inputCls} pl-10`} style={inputStyle} data-testid="email-input" />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 rounded-lg font-semibold text-sm tracking-wide" style={{ background: 'var(--club-green)', color: '#fff' }} disabled={loading} data-testid="find-account-btn">
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          )}

          {/* Step 2: 6-digit code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>6-Digit Code</Label>
                <div className="flex gap-2 justify-center" onPaste={handleCodePaste}>
                  {code.map((digit, i) => (
                    <Input
                      key={i}
                      ref={el => codeRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeInput(i, e.target.value)}
                      onKeyDown={e => handleCodeKeyDown(i, e)}
                      className="w-11 h-14 text-center text-xl font-bold rounded-lg border"
                      style={{ borderColor: digit ? 'var(--club-green)' : 'var(--fairway-sand)', color: '#000', background: digit ? 'rgba(15,59,46,0.05)' : 'white' }}
                      data-testid={`code-input-${i}`}
                    />
                  ))}
                </div>
                <p className="text-xs text-center" style={{ color: 'var(--warm-gray)' }}>Check your email for the code. It expires in 15 minutes.</p>
              </div>
              <Button type="submit" className="w-full h-12 rounded-lg font-semibold text-sm tracking-wide" style={{ background: 'var(--club-green)', color: '#fff' }} disabled={loading || code.join('').length !== 6} data-testid="verify-code-btn">
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <button type="button" onClick={() => { setCode(['','','','','','']); handleFindAccount({ preventDefault: () => {} }); }} className="w-full text-center text-xs hover:underline" style={{ color: 'var(--club-green)' }} data-testid="resend-code-btn">
                Didn't get a code? Send again
              </button>
            </form>
          )}

          {/* Step 3: New password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="p-4 rounded-lg flex items-center gap-3" style={{ background: 'var(--screen-bg)', border: '1px solid var(--fairway-sand)' }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--club-green)' }}>
                  <Check className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--warm-gray)' }}>Your username:</p>
                  <p className="font-semibold" style={{ color: 'var(--deep-forest)' }}>{username}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>New Password</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className={`${inputCls} pr-10`} style={inputStyle} data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40 hover:opacity-70">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>Confirm Password</Label>
                <Input type={showPassword ? 'text' : 'password'} placeholder="Confirm password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className={inputCls} style={inputStyle} data-testid="confirm-password-input" />
              </div>
              <div className="space-y-1.5 py-1">
                {[
                  { met: newPassword.length >= 8, label: 'At least 8 characters' },
                  { met: newPassword === confirmPassword && confirmPassword.length > 0, label: 'Passwords match' }
                ].map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${r.met ? 'bg-[var(--club-green)] text-white' : 'bg-[var(--stone)]'}`}>
                      {r.met && <Check className="w-3 h-3" />}
                    </div>
                    <span style={{ color: r.met ? 'var(--deep-forest)' : 'var(--warm-gray)' }}>{r.label}</span>
                  </div>
                ))}
              </div>
              <Button type="submit" className="w-full h-12 rounded-lg font-semibold text-sm tracking-wide" style={{ background: 'var(--club-green)', color: '#fff' }} disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword} data-testid="reset-password-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          <p className="text-center text-sm mt-6" style={{ color: 'var(--warm-gray)' }}>
            Remember your password?{' '}
            <Link to="/login" className="font-semibold hover:underline" style={{ color: 'var(--club-green)' }} data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
